'use client';

import { forwardRef, type HTMLAttributes, type JSX, type MouseEvent, useCallback, useRef } from 'react';
import { DraggableCore, type DraggableEventHandler } from 'react-draggable';
import { twMerge } from 'tailwind-merge';

import { KEYBOARD_SHIFT_STEP_SECONDS, KEYBOARD_STEP_SECONDS } from './constants';

interface TrimHandleProps {
    position: 'left' | 'right';
    onDragStart: (screenX: number) => void;
    onDrag: (screenX: number) => void;
    onDragStop: () => void;
    /** Keyboard resize, in seconds. Pixels would round away on short videos. */
    onNudge: (deltaSeconds: number) => void;
    disabled?: boolean;
    className?: string;
}

type TrimHandleAllProps = TrimHandleProps &
    Omit<HTMLAttributes<HTMLDivElement>, 'onDragStart' | 'onDrag' | 'onDragStop'>;

const getScreenX = (e: globalThis.MouseEvent | TouchEvent): number => {
    if ('screenX' in e) return e.screenX;
    if ('touches' in e && e.touches.length > 0) return e.touches[0].screenX;
    return 0;
};

/**
 * Stops propagation so the parent DraggableCore does not also treat a trim as a
 * whole-selection move. Forwards its ref for use as a Radix Tooltip trigger.
 */
export const TrimHandle = forwardRef<HTMLDivElement, TrimHandleAllProps>(function TrimHandle(
    { position, onDragStart, onDrag, onDragStop, onNudge, disabled = false, className = '', ...rest },
    ref,
): JSX.Element {
    const lastScreenX = useRef<number>(0);
    const nodeRef = useRef<HTMLDivElement>(null);

    // DraggableCore needs a RefObject; Radix Tooltip needs the forwarded ref. Feed both.
    const setRefs = useCallback(
        (node: HTMLDivElement | null): void => {
            nodeRef.current = node;
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                ref.current = node;
            }
        },
        [ref],
    );

    const handleStart: DraggableEventHandler = useCallback(
        (e) => {
            e.stopPropagation();
            const screenX = getScreenX(e);
            lastScreenX.current = screenX;
            onDragStart(screenX);
        },
        [onDragStart],
    );

    const handleDrag: DraggableEventHandler = useCallback(
        (e) => {
            e.stopPropagation();
            onDrag(getScreenX(e));
        },
        [onDrag],
    );

    const handleStop: DraggableEventHandler = useCallback(
        (e) => {
            e.stopPropagation();
            onDragStop();
        },
        [onDragStop],
    );

    const handleMouseDown = useCallback((e: MouseEvent) => {
        e.stopPropagation();
    }, []);

    return (
        <DraggableCore
            nodeRef={nodeRef}
            disabled={disabled}
            onStart={handleStart}
            onDrag={handleDrag}
            onStop={handleStop}
        >
            <div
                ref={setRefs}
                {...rest}
                className={twMerge(
                    // Matches the width of the yellow end cap it sits over.
                    'absolute inset-y-0 w-[11px] z-20',
                    'flex items-center justify-center',
                    position === 'left' ? 'left-0' : 'right-0',
                    disabled && 'opacity-50',
                    className,
                )}
                style={{ cursor: disabled ? 'not-allowed' : 'col-resize' }}
                data-testid={`trim-handle-${position}`}
                role="button"
                aria-label={`${position === 'left' ? 'Start' : 'End'} trim handle. Use arrow keys to adjust.`}
                tabIndex={disabled ? -1 : 0}
                onMouseDown={handleMouseDown}
                onTouchStart={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    if (disabled) return;
                    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

                    // Stop propagation so the parent does not also slide the window.
                    e.preventDefault();
                    e.stopPropagation();

                    const step = e.shiftKey ? KEYBOARD_SHIFT_STEP_SECONDS : KEYBOARD_STEP_SECONDS;
                    onNudge(e.key === 'ArrowLeft' ? -step : step);
                }}
            />
        </DraggableCore>
    );
});
